import React, { useMemo } from 'react';
import { Text } from '@amzn/storm-ui';
import {
  AccordionButtonInnerWrapper,
  AccordionButtonOuterWrapper,
  AccordionDropdownIcon,
  HiddenContent,
  SvgWrapper,
} from '../styles';
import { SvgIcon } from 'src/commons/components/SvgIcon/SvgIcon';
import { NavMetricPublisher } from 'src/commons/utils/metrics';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';

interface AccordionButtonProps {
  id: string;
  cbSelected?: boolean;
}

const AccordionButton: React.VFC<AccordionButtonProps> = ({
  id,
  cbSelected = false,
}) => {
  const bundle = useSidenavBundle();
  const {
    setActiveCategoryId,
    isNavOpen,
    activeCategoryId,
    selectedNavItemId,
    toolbarItems,
  } = useConsolidatedNavContext();

  const toolbarItem = useMemo(
    () => toolbarItems?.find((toolbarItem) => toolbarItem.id === id),
    [toolbarItems],
  );

  if (!toolbarItem) {
    return null;
  }

  const iconPath = toolbarItem.iconPath;
  //TODO: remove after UnifiedIngress dial-up
  const isSelected =
    selectedNavItemId === id ||
    (id === 'aac-toolbar-item-sponsored-ads' && cbSelected);
  const isExpanded = activeCategoryId === id;
  const title = toolbarItem.toolTip;

  const ariaLabel = (
    !isExpanded
      ? bundle.getMessage('sidenav-show-links-aria-label')
      : bundle.getMessage('sidenav-hide-links-aria-label')
  ).replace('{title}', title ?? '');

  const handleOnclick = (): void => {
    NavMetricPublisher.publishCounterMonitor(`toolbarItemClicked-${id}`, 1);
    setActiveCategoryId((activeCategory) => (id === activeCategory ? '' : id));
  };

  return (
    <AccordionButtonOuterWrapper isSelected={isSelected}>
      <AccordionButtonInnerWrapper
        isSelected={isSelected}
        isNavOpen={isNavOpen}
        data-ccx-e2e-id={id}
        aria-label={ariaLabel}
        aria-expanded={isExpanded}
        onClick={handleOnclick}
        {...getTaktAttributes(id, chromeComponents.NAV)}
      >
        <SvgWrapper
          isSelected={isSelected}
          alignItems="center"
          justifyContent="center"
        >
          <SvgIcon
            iconPath={iconPath}
            ariaLabel={title}
            isSelected={isSelected}
          />
        </SvgWrapper>
        {isNavOpen && (
          <>
            <HiddenContent>
              <Text renderAs="span">{title}</Text>
            </HiddenContent>
            <AccordionDropdownIcon
              type={isExpanded ? 'angle-down' : 'angle-right'}
            />
          </>
        )}
      </AccordionButtonInnerWrapper>
    </AccordionButtonOuterWrapper>
  );
};

export default AccordionButton;
